import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import PoweredBy from '../PoweredBy';

interface RecommendedProduct {
  id: string;
  name: string;
  category: string;
  originalPrice: number;
  discountPercent: number;
  image: string;
  reason: string;
}

// Personalized recommendations based on returned items
const RECOMMENDATIONS: RecommendedProduct[] = [
  {
    id: 'align-pant-new',
    name: 'Align High-Rise Pant 25"',
    category: 'New Arrival',
    originalPrice: 98,
    discountPercent: 15,
    image: '/assets/images/lululemon/offer-align.jpg',
    reason: 'Based on your trade-in'
  },
  {
    id: 'scuba-new-color',
    name: 'Scuba Oversized Half-Zip',
    category: 'New Color',
    originalPrice: 118,
    discountPercent: 10,
    image: '/assets/images/lululemon/offer-scuba.jpg',
    reason: 'Trending this season'
  },
  {
    id: 'energy-bra',
    name: 'Energy Bra High Support',
    category: 'Best Seller',
    originalPrice: 68,
    discountPercent: 20,
    image: '/assets/images/lululemon/offer-bra.jpg',
    reason: 'Pairs well with your style'
  }
];

const LululemonOfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [selectedProduct, setSelectedProduct] = useState<RecommendedProduct | null>(null);

  // Calculate total trade-in value from state
  const totalTradeInValue = state.empties.reduce((sum, empty) => {
    if (empty.product.rewards) {
      return sum + parseFloat(empty.product.rewards.dollarValue.replace('$', ''));
    }
    return sum;
  }, 0);

  const handleSelectProduct = (product: RecommendedProduct) => {
    setSelectedProduct(product);
  };

  const handleClaimOffer = () => {
    dispatch({ type: 'SET_CLAIMED_OFFER', payload: true });
    navigate('/lululemon/confirmation');
  };

  const handleNoThanks = () => {
    dispatch({ type: 'SET_CLAIMED_OFFER', payload: false });
    navigate('/lululemon/confirmation');
  };

  const getDiscountedPrice = (original: number, discount: number) => {
    return (original * (1 - discount / 100)).toFixed(2);
  };

  const getPriceAfterCredit = (original: number, discount: number) => {
    const discounted = original * (1 - discount / 100);
    const afterCredit = Math.max(0, discounted - totalTradeInValue);
    return afterCredit.toFixed(2);
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: '#1A1A1A' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, #D31334 0%, transparent 70%)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[1200px] flex justify-between items-center">
            <img 
              src="/assets/images/logos/lululemon-white.png" 
              alt="lululemon" 
              className="h-8"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <div 
              className="px-4 py-2 rounded-full"
              style={{ backgroundColor: 'rgba(211,19,52,0.2)' }}
            >
              <span className="text-white font-medium">${totalTradeInValue} Credit Available</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center px-20 py-8">
          {/* Heading */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 
              className="text-5xl font-light mb-4"
              style={{ color: '#FFFFFF', fontFamily: "'Calibre', 'Helvetica Neue', sans-serif" }}
            >
              Put Your Credit to Work
            </h1>
            <p 
              className="text-xl font-light"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Here are some picks we think you'll love
            </p>
          </motion.div>

          {/* Product Recommendations */}
          <motion.div
            className="grid grid-cols-3 gap-8 max-w-5xl w-full mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {RECOMMENDATIONS.map((product, index) => (
              <motion.div
                key={product.id}
                className="relative rounded-2xl overflow-hidden cursor-pointer group"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: selectedProduct?.id === product.id 
                    ? '2px solid #D31334' 
                    : '1px solid rgba(255,255,255,0.1)'
                }}
                onClick={() => handleSelectProduct(product)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                {/* Discount Badge */}
                <div 
                  className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium z-10"
                  style={{ backgroundColor: '#D31334', color: '#FFFFFF' }}
                >
                  {product.discountPercent}% OFF
                </div>

                {/* Selection indicator */}
                {selectedProduct?.id === product.id && (
                  <motion.div
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10"
                    style={{ backgroundColor: '#D31334' }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                )}

                {/* Image */}
                <div className="h-56 bg-white overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain transition-transform group-hover:scale-105"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <span className="text-white/50 text-xs uppercase tracking-wider">{product.category}</span>
                  <h3 className="text-white text-lg font-medium mt-1 mb-2">{product.name}</h3>
                  <p className="text-white/50 text-sm mb-4">{product.reason}</p>
                  
                  {/* Pricing */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="text-white/40 line-through">${product.originalPrice}</span>
                      <span className="text-white text-lg font-medium">${getDiscountedPrice(product.originalPrice, product.discountPercent)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">After credit:</span>
                      <span className="text-xl font-bold" style={{ color: '#D31334' }}>
                        ${getPriceAfterCredit(product.originalPrice, product.discountPercent)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <button
              onClick={handleNoThanks}
              className="px-8 py-4 rounded-lg text-white/70 hover:text-white uppercase tracking-wider transition-colors"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              No Thanks, Just My Credit
            </button>
            <button
              onClick={handleClaimOffer}
              disabled={!selectedProduct}
              className="px-8 py-4 rounded-lg text-white uppercase tracking-wider font-medium transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#D31334' }}
            >
              {selectedProduct ? `Get ${selectedProduct.name.split(' ').slice(0, 2).join(' ')}` : 'Select a Product'}
            </button>
          </motion.div>

          {/* Credit note */}
          <motion.p
            className="text-white/40 text-sm mt-8 text-center max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Your ${totalTradeInValue} credit will be applied to your purchase or sent to your account if you skip the offer.
          </motion.p>
        </div>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <PoweredBy />
        </div>
      </div>
    </motion.div>
  );
};

export default LululemonOfferView;

